"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, type ReactNode } from "react";
import { ResponsiveContainer, Tooltip } from "recharts";
import { cx } from "@/ds/cx";
import type { ComponentPropsWithoutRef } from "react";

/**
 * Series names → label and colour. Each key becomes a CSS variable
 * `--color-<key>` inside the chart, so series can use `var(--color-<key>)`.
 * Give `theme` instead of `color` to vary the colour with dark mode.
 */
export type ChartConfig = Record<string, {
  label?: ReactNode;
  color?: string;
  theme?: {
    light: string;
    dark: string;
  };
}>;
function variables(config: ChartConfig, mode: "light" | "dark") {
  return Object.entries(config).map(([key, entry]) => {
    const colour = entry.theme?.[mode] ?? entry.color;
    return colour ? `--color-${key}: ${colour};` : "";
  }).join("");
}

/** Sizes a recharts chart to its box and defines the series colour variables. */
export function ChartContainer(t0) {
  const $ = _c(25);
  let children;
  let className;
  let config;
  let id;
  let rest;
  if ($[0] !== t0) {
    ({
      id,
      config,
      className,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = config;
    $[4] = id;
    $[5] = rest;
  } else {
    children = $[1];
    className = $[2];
    config = $[3];
    id = $[4];
    rest = $[5];
  }
  const auto = useId();
  const t1 = id ?? auto;
  let t2;
  if ($[6] !== t1) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /[^a-zA-Z0-9_-]/g;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t1.replace(t3, "");
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const chartId = `chart-${t2}`;
  const scope = `[data-chart="${chartId}"]`;
  let t3;
  if ($[9] !== className) {
    t3 = cx("flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border/60 [&_.recharts-surface]:outline-none", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== config) {
    t4 = variables(config, "light");
    $[11] = config;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== config) {
    t5 = variables(config, "dark");
    $[13] = config;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = `${scope}{${t4}}.dark ${scope}{${t5}}`;
  let t7;
  if ($[15] !== t6) {
    t7 = <style>{t6}</style>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== children) {
    t8 = <ResponsiveContainer>{children}</ResponsiveContainer>;
    $[17] = children;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== chartId || $[20] !== rest || $[21] !== t3 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div data-chart={chartId} className={t3} {...rest}>{t7}{t8}</div>;
    $[19] = chartId;
    $[20] = rest;
    $[21] = t3;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export const ChartTooltip = Tooltip;
interface TooltipItem {
  name?: string | number;
  value?: number | string | (number | string)[];
  dataKey?: string | number;
  color?: string;
  payload?: Record<string, string | number | undefined>;
}

/**
 * Tooltip body for ChartTooltip's `content`. recharts fills in `active`,
 * `payload` and `label`; pages pass the formatters.
 */
export function ChartTooltipContent(t0) {
  const $ = _c(15);
  const {
    active,
    payload,
    label,
    labelFormatter,
    formatter,
    className
  } = t0;
  if (!active || !payload?.length) {
    return null;
  }
  let t1;
  if ($[0] !== label || $[1] !== labelFormatter || $[2] !== payload) {
    t1 = labelFormatter ? labelFormatter(label as never, payload) : label;
    $[0] = label;
    $[1] = labelFormatter;
    $[2] = payload;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const heading = t1;
  let t2;
  if ($[4] !== className) {
    t2 = cx("grid min-w-[9rem] gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-lg", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== heading) {
    t3 = heading ? <div className="font-medium">{heading}</div> : null;
    $[6] = heading;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== formatter || $[9] !== payload) {
    t4 = payload.map((item, index) => {
      const key = String(item.dataKey ?? item.name ?? index);
      return <div key={key} className="flex items-center gap-2">{formatter ? formatter(item.value as never, item.name as never, item, index, payload) : <><span className="size-2.5 shrink-0 rounded-[2px]" style={{
            background: item.color ?? `var(--color-${key})`
          }} /><span className="text-muted-foreground">{item.name}</span><span className="ml-auto font-mono font-medium tabular-nums">{Array.isArray(item.value) ? item.value.join(", ") : String(item.value ?? "")}</span></>}</div>;
    });
    $[8] = formatter;
    $[9] = payload;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}